import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  X,
  Check,
  ChevronDown,
  Search,
  Mail,
  Link2,
  Copy,
  Send,
} from "lucide-react";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${window.location.origin}/api`;

export default function GroupMembersModal({ group, onClose, onMembersChange, currentUser }) {
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [inviteLink, setInviteLink] = useState("");
  const [copied, setCopied] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [composeEmail, setComposeEmail] = useState("");
  const [composeMessage, setComposeMessage] = useState("");
  const emailRef = useRef(null);

  useEffect(() => {
    if (group) {
      fetchMembers();
      fetchInviteLink();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group]);

  const fetchMembers = async () => {
    try {
      const res = await fetch(`${API}/groups/${group.group_id}/members`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setMembers(data);
        onMembersChange?.(group.group_id, data);
      }
    } catch (err) {
      console.error("Failed to fetch members:", err);
    }
  };

  const fetchInviteLink = async () => {
    try {
      const res = await fetch(`${API}/groups/${group.group_id}/invite-link`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        // Always build the invite URL from the current origin so it stays correct
        // regardless of what the backend's FRONTEND_URL env var is set to.
        const code = data.invite_code || (data.invite_link || "").split("/join/").pop();
        setInviteLink(`${window.location.origin}/#/join/${code}`);
      }
    } catch (err) {
      console.error("Failed to fetch invite link:", err);
    }
  };

  const handleInviteByEmail = async () => {
    if (!emailInput.trim()) return;
    try {
      const res = await fetch(`${API}/groups/${group.group_id}/invite-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ emails: emailInput.trim() }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.added.length > 0) {
          setMembers((prev) => [...prev, ...data.added]);
          onMembersChange?.(group.group_id, [...members, ...data.added]);
          toast.success(`${data.added.length} member(s) invited`);
        }
        if (data.skipped.length > 0) {
          toast.info(`${data.skipped.length} already in group`);
        }
        setEmailInput("");
      }
    } catch {
      toast.error("Failed to invite");
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    toast.success("Link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleChangeRole = async (memberId, newRole) => {
    try {
      const res = await fetch(`${API}/groups/${group.group_id}/members/${memberId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ role: newRole }),
      });
      if (res.ok) {
        const updated = await res.json();
        setMembers((prev) => prev.map((m) => (m.member_id === memberId ? updated : m)));
        toast.success(`Role updated to ${newRole}`);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to update role");
      }
    } catch {
      toast.error("Failed to update role");
    }
  };

  const handleRemove = async (memberId) => {
    try {
      const res = await fetch(`${API}/groups/${group.group_id}/members/${memberId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        const newMembers = members.filter((m) => m.member_id !== memberId);
        setMembers(newMembers);
        onMembersChange?.(group.group_id, newMembers);
        toast.success("Member removed");
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to remove");
      }
    } catch {
      toast.error("Failed to remove");
    }
  };

  const filtered = members.filter(
    (m) =>
      m.name.toLowerCase().includes(search.toLowerCase()) ||
      m.email.toLowerCase().includes(search.toLowerCase())
  );

  if (!group) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="group-members-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      <div className="relative w-full max-w-lg bg-[#1e2126] rounded-xl border border-white/10 flex flex-col max-h-[85vh] shadow-2xl">
        {/* Header */}
        <div className="px-6 pt-5 pb-1">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-lg font-semibold text-white" data-testid="group-modal-title">{group.name}</h2>
            <button
              data-testid="group-modal-close"
              onClick={onClose}
              className="p-1 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
          <p className="text-base font-medium text-white">Members</p>
          <p className="text-xs text-gray-500 mt-0.5">Additional charges may apply</p>
        </div>

        {/* Invite with email */}
        <div className="px-6 pt-4">
          <div className="flex items-center gap-2 mb-2">
            <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <span className="text-sm text-white font-medium">Invite with email</span>
          </div>
          <div className="flex items-center gap-2">
            <Input
              ref={emailRef}
              data-testid="invite-email-input"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder="Add emails, separated by commas or spaces"
              className="flex-1 bg-transparent border-white/10 text-white placeholder:text-gray-500 h-9 text-sm"
              onKeyDown={(e) => { if (e.key === "Enter") handleInviteByEmail(); }}
            />
            <button
              data-testid="invite-email-btn"
              onClick={handleInviteByEmail}
              className="text-sm font-medium text-blue-400 hover:text-blue-300 px-2 whitespace-nowrap transition-colors"
            >
              Invite
            </button>
          </div>
        </div>

        {/* Send Email Invite (via Gmail) */}
        <div className="px-6 pt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Send className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="text-sm text-white font-medium">Send email invite</span>
            </div>
            {!showCompose && (
              <button
                data-testid="compose-email-btn"
                onClick={() => setShowCompose(true)}
                className="text-sm font-medium text-blue-400 hover:text-blue-300 transition-colors"
              >
                Compose
              </button>
            )}
          </div>
          {showCompose && (
            <div className="space-y-2 bg-white/5 rounded-lg p-3 border border-white/10">
              <div>
                <label className="text-[11px] text-gray-500 uppercase tracking-wider">From</label>
                <p className="text-sm text-gray-300">{currentUser?.email || "Your email"}</p>
              </div>
              <div>
                <label className="text-[11px] text-gray-500 uppercase tracking-wider">To</label>
                <Input
                  data-testid="compose-to-input"
                  value={composeEmail}
                  onChange={(e) => setComposeEmail(e.target.value)}
                  placeholder="recipient@email.com"
                  className="bg-transparent border-white/10 text-white placeholder:text-gray-500 h-8 text-sm mt-1"
                />
              </div>
              <div>
                <label className="text-[11px] text-gray-500 uppercase tracking-wider">Message (optional)</label>
                <textarea
                  data-testid="compose-message-input"
                  value={composeMessage}
                  onChange={(e) => setComposeMessage(e.target.value)}
                  placeholder="Add a personal message..."
                  rows={2}
                  className="w-full bg-transparent border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-gray-500 mt-1 resize-none focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div className="flex gap-2 justify-end pt-1">
                <button
                  onClick={() => { setShowCompose(false); setComposeEmail(""); setComposeMessage(""); }}
                  className="text-sm text-gray-400 hover:text-gray-300 px-3 py-1.5 transition-colors"
                >
                  Cancel
                </button>
                <button
                  data-testid="send-email-invite-btn"
                  onClick={() => {
                    if (!composeEmail.trim()) {
                      toast.error("Please enter a recipient email");
                      return;
                    }
                    if (!inviteLink) {
                      toast.error("Invite link not ready yet");
                      return;
                    }
                    const subject = encodeURIComponent(`You're invited to join "${group.name}"`);
                    const senderName = currentUser?.name || "Your teammate";
                    let body = `Hi,\n\n${senderName} has invited you to join the group "${group.name}".\n\nClick the link below to join:\n${inviteLink}`;
                    if (composeMessage.trim()) {
                      body = `Hi,\n\n${senderName} has invited you to join the group "${group.name}".\n\nPersonal message:\n"${composeMessage.trim()}"\n\nClick the link below to join:\n${inviteLink}`;
                    }
                    body += `\n\nSee you there!`;
                    const encodedBody = encodeURIComponent(body);
                    const to = encodeURIComponent(composeEmail.trim());
                    // Open Gmail compose
                    const gmailUrl = `https://mail.google.com/mail/?view=cm&to=${to}&su=${subject}&body=${encodedBody}`;
                    window.open(gmailUrl, "_blank");
                    toast.success("Gmail opened — review and hit Send!");
                    setShowCompose(false);
                    setComposeEmail("");
                    setComposeMessage("");
                  }}
                  className="flex items-center gap-1.5 text-sm font-medium bg-blue-500 hover:bg-blue-600 text-white px-4 py-1.5 rounded-lg transition-colors"
                >
                  <Send className="w-3.5 h-3.5" />
                  Send via Gmail
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Invite with link */}
        <div className="px-6 pt-4">
          <div className="flex items-center gap-2 mb-2">
            <Link2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <span className="text-sm text-white font-medium">Invite with link</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-transparent border border-white/10 rounded-md px-3 py-2 text-sm text-gray-400 truncate h-9 flex items-center">
              {inviteLink || "Loading..."}
            </div>
            <button
              data-testid="copy-link-btn"
              onClick={handleCopyLink}
              className="text-sm font-medium text-blue-400 hover:text-blue-300 px-2 whitespace-nowrap transition-colors"
            >
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="px-6 pt-4">
          <div className="relative">
            <Input
              data-testid="member-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email"
              className="bg-transparent border-white/10 text-white placeholder:text-gray-500 h-9 text-sm pr-10"
            />
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          </div>
        </div>

        {/* Member List */}
        <ScrollArea className="flex-1 mt-3">
          <div className="px-6">
            {filtered.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-6">
                {search ? `No members match "${search}"` : "No members yet. Invite someone above!"}
              </p>
            )}
            {filtered.map((member) => (
              <div
                key={member.member_id}
                data-testid={`group-member-${member.member_id}`}
                className="flex items-center gap-3 py-2.5"
              >
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium text-white flex-shrink-0"
                  style={{ backgroundColor: member.color }}
                >
                  {member.initials}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{member.name}</p>
                  <p className="text-xs text-gray-500 truncate">{member.email}</p>
                </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300 transition-colors">
                      {member.role === "admin" ? "Admin" : "Member"}
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-[#2d3136] border-white/10">
                    <DropdownMenuItem
                      className="text-white"
                      onClick={() => handleChangeRole(member.member_id, "member")}
                    >
                      <Check className={cn("w-4 h-4 mr-2", member.role !== "member" && "opacity-0")} />
                      Member
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-white"
                      onClick={() => handleChangeRole(member.member_id, "admin")}
                    >
                      <Check className={cn("w-4 h-4 mr-2", member.role !== "admin" && "opacity-0")} />
                      Admin
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-red-400"
                      onClick={() => handleRemove(member.member_id)}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Remove
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>
        </ScrollArea>

        {/* Save Button */}
        <div className="px-6 py-4 flex justify-end">
          <Button
            data-testid="group-modal-save"
            className="bg-blue-500 hover:bg-blue-600 px-8"
            onClick={onClose}
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}
